import { useId } from 'react';
import {
  buildProjectBadgeCheckboxFrameSx,
  buildProjectBadgeRootSx,
  projectBadgeCheckboxSx,
  projectBadgeHelperTextSx,
  projectBadgeLabelSx,
  projectBadgePreviewFrameSx,
  type ProjectBadgeFieldTextColumnWidth,
} from '@/features/projects/components/ProjectBadgeField.styles';
import { ColourSwatchSelector } from '@/features/shared/components/ColourSwatchSelector';
import {
  normalizeProjectBadgeChars,
  projectBadgeCharsMaxLength,
  projectBadgeColorOptions,
} from '@/shared/data/projectBadges';
import { s } from '@/theme/tokens';
import { Box } from '@/ui';
import { Checkbox, FormHelperText, Text, TextField } from '@/ui/primitives';
import type { ProjectBadgeColor } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectBadgeFieldProps = {
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  chars: string;
  color: ProjectBadgeColor;
  onCharsChange: (value: string) => void;
  onColorChange: (value: ProjectBadgeColor) => void;
  disabled?: boolean;
  textFieldColumnWidth?: ProjectBadgeFieldTextColumnWidth;
  checkboxMarginLeft?: string;
  preview?: ReactElement;
};

export const ProjectBadgeField = ({
  enabled,
  onEnabledChange,
  chars,
  color,
  onCharsChange,
  onColorChange,
  disabled = false,
  textFieldColumnWidth = 'minmax(0, 1fr)',
  checkboxMarginLeft = s('none'),
  preview,
}: ProjectBadgeFieldProps): ReactElement => {
  const fieldId = useId();
  const controlsDisabled = disabled || !enabled;

  return (
    <Box sx={buildProjectBadgeRootSx(textFieldColumnWidth)}>
      <Text role="body" component="label" htmlFor={fieldId} sx={projectBadgeLabelSx}>
        Badge
      </Text>
      <Box />
      <Box />
      <Box />
      <TextField
        id={fieldId}
        fieldStyle="settings"
        inputHeight={34}
        value={chars}
        placeholder="P"
        slotProps={{ htmlInput: { maxLength: projectBadgeCharsMaxLength } }}
        disabled={controlsDisabled}
        onChange={(event) => {
          onCharsChange(normalizeProjectBadgeChars(event.target.value));
        }}
      />
      <ColourSwatchSelector
        ariaLabel="Choose project badge colour"
        disabled={controlsDisabled}
        value={color}
        options={projectBadgeColorOptions}
        onChange={onColorChange}
      />
      {preview ? <Box sx={projectBadgePreviewFrameSx}>{preview}</Box> : <Box />}
      <Box sx={buildProjectBadgeCheckboxFrameSx(checkboxMarginLeft)}>
        <Checkbox
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onEnabledChange(event.target.checked);
          }}
          slotProps={{ input: { 'aria-label': 'Enable project badge' } }}
          sx={projectBadgeCheckboxSx}
        />
      </Box>
      <FormHelperText sx={projectBadgeHelperTextSx}>Up to 2 characters.</FormHelperText>
      <Box />
      <Box />
      <Box />
    </Box>
  );
};
